import { useTranslation } from 'react-i18next';
import { Calendar } from 'lucide-react';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { CollapsibleStats } from '@/components/core';
import { cn } from '@/lib/utils';
import { useGetToilEarnedSummary } from '../../hooks/use-toil';

export interface ToilSummaryTerm {
  /** Period bounds, plain "YYYY-MM-DD", both inclusive. */
  periodStart: string;
  periodEnd: string;
  toilCarryForwardDays: number;
  specialToilCarryForwardDays: number;
}

const TONES = {
  green: 'bg-success-subtle text-success',
  amber: 'bg-warning-subtle text-warning',
  blue: 'bg-accent text-primary',
};

const GRID = 'grid grid-cols-[repeat(auto-fit,minmax(14rem,1fr))] gap-3';

const SummaryCard = ({
  days,
  label,
  tone,
}: {
  days: number;
  label: string;
  tone: keyof typeof TONES;
}) => {
  const { t } = useTranslation();
  return (
    <div className={cn('flex items-center gap-4 rounded-lg px-6 py-5', TONES[tone])}>
      <Calendar className="size-6 shrink-0" />
      <div className="flex min-w-0 flex-col">
        <span className="text-xl font-bold tabular-nums text-foreground">
          {days} {t('DAYS')}
        </span>
        <span className="text-sm text-foreground">{label}</span>
      </div>
    </div>
  );
};

/**
 * The legacy My TOIL cards for one employee's HR term: TOIL carried into the term (special only when
 * there is some) and TOIL earned in it (requests that aren't rejected), weekend and special apart by
 * each request's `ToilCategory`.
 */
export const ToilSummaryCards = ({ userId, term }: { userId: string; term: ToilSummaryTerm }) => {
  const { t } = useTranslation();
  const { data: earned, isLoading } = useGetToilEarnedSummary(
    userId,
    term.periodStart,
    term.periodEnd
  );

  if (isLoading || !earned) {
    return (
      <div className={GRID}>
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={index} className="h-20 rounded-lg" />
        ))}
      </div>
    );
  }

  // On a phone only the first card shows; the chevron opens the rest (old portal's stat collapse).
  return (
    <CollapsibleStats
      className="gap-3 md:grid-cols-[repeat(auto-fit,minmax(14rem,1fr))]"
      items={[
        <SummaryCard
          key="carry-forward"
          tone="green"
          days={term.toilCarryForwardDays}
          label={t('TOIL_CARRY_FORWARD')}
        />,
        ...(term.specialToilCarryForwardDays > 0
          ? [
              <SummaryCard
                key="special-carry-forward"
                tone="green"
                days={term.specialToilCarryForwardDays}
                label={t('SPECIAL_TOIL_CARRY_FORWARD')}
              />,
            ]
          : []),
        <SummaryCard
          key="special-earned"
          tone="amber"
          days={earned.special}
          label={t('SPECIAL_TOIL_EARNED')}
        />,
        <SummaryCard
          key="weekend-earned"
          tone="blue"
          days={earned.weekend}
          label={t('WEEKEND_TOIL_EARNED')}
        />,
      ]}
    />
  );
};
